import React, { memo } from 'react';
import { StyleSheet } from 'react-native';

import { LI, UL } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

import { useA11ySelector } from '@sg/a11y-helpers';

import { type AddressDetails } from '../../../locations.types';
import { LocationSearchAddress } from '../LocationSearchAddress';
import { LocationSearchScrollView } from '../LocationSearchScrollView';

export const LocationSearchAddressesList = memo((props: LocationSearchAddressesListProps) => {
  const { addresses, onPress } = props;

  const { match } = useResponsive();
  const a11ySelector = useA11ySelector('locations.delivery.addresses-container'); // prettier-ignore

  // ─── Styles ────────────────────────────────────────────────────────

  const containerStyles = match([styles.containerXS, undefined]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <LocationSearchScrollView
      style={containerStyles}
      contentContainerStyle={styles.contentContainer}
      a11ySelector={a11ySelector}
    >
      <UL>
        {addresses.map((customerAddress) => {
          const address = {
            id: customerAddress.addressId,
            address: customerAddress.formattedAddress,
            name: customerAddress.name,
            street: customerAddress.street,
            city: customerAddress.city,
            zipCode: customerAddress.zipCode,
            state: customerAddress.state,
            country: customerAddress.country,
            latitude: customerAddress.latitude,
            longitude: customerAddress.longitude,
          };

          return (
            <LI key={customerAddress.addressId}>
              <LocationSearchAddress
                variation="customer-address"
                address={address}
                addressDetails={customerAddress}
                onPress={onPress}
              />
            </LI>
          );
        })}
      </UL>
    </LocationSearchScrollView>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
  },
  contentContainer: {
    padding: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchAddressesListProps = {
  addresses: AddressDetails[] | readonly AddressDetails[];
  onPress: (addressDetails: AddressDetails) => void;
};
